import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, TextField, Chip, Alert, Paper } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useLanguage } from '../context/LanguageContext';

export default function TokenDisplayView() {
    const { t, currentLanguage } = useLanguage();
    const [preparingTokens, setPreparingTokens] = useState(['108', '110', '112', '115']);
    const [readyTokens, setReadyTokens] = useState(['102', '105', '106']);
    const [newToken, setNewToken] = useState('');
    const [voiceAlert, setVoiceAlert] = useState('');

    const handleAddToken = () => {
        if (newToken.trim()) {
            setPreparingTokens(prev => [...prev, newToken.trim()]);
            setNewToken('');
        }
    };

    const handleMarkReady = (token) => {
        setPreparingTokens(prev => prev.filter(t => t !== token));
        if (!readyTokens.includes(token)) {
            setReadyTokens(prev => [...prev, token]);
            setVoiceAlert(`Token Number ${token} is ready! Please collect.`);
            setTimeout(() => setVoiceAlert(''), 5000);
        }
    };

    const handleDeliverToken = (token) => {
        setReadyTokens(prev => prev.filter(t => t !== token));
    };

    return (
        <Box sx={{ p: 4, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header info */}
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#008751' }}>
                {currentLanguage === 'mr' ? 'ग्राहक टोकन डिस्प्ले' : currentLanguage === 'hi' ? 'ग्राहक टोकन डिस्प्ले' : 'Customer Token Display'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                {currentLanguage === 'mr'
                    ? 'ग्राहकांना ऑर्डर संकलनासाठी सूचित करणारी टीव्ही (TV Screen) रचना.'
                    : currentLanguage === 'hi'
                    ? 'ग्राहकों को ऑर्डर प्राप्त करने के लिए सूचित करने वाली टीवी स्क्रीन डिज़ाइन।'
                    : 'A mockup of the TV screen showing queue tokens. Click to simulate kitchen readiness.'}
            </Typography>

            {voiceAlert && (
                <Alert 
                    icon={<NotificationsActiveIcon />} 
                    severity="success" 
                    sx={{ mb: 3, borderRadius: 2, fontWeight: 800, fontSize: '1.1rem', animation: 'pulse 1.5s infinite' }}
                >
                    📣 {voiceAlert}
                </Alert>
            )}

            {/* Simulated TV Display Section */}
            <Paper sx={{ 
                bgcolor: '#0f172a', // Dark theme for TV Screen
                color: '#fff', 
                borderRadius: 4, 
                overflow: 'hidden', 
                border: '8px solid #334155',
                mb: 4,
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
            }}>
                {/* TV Header Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, bgcolor: '#008751' }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#fff' }}>
                        DINEXA FOOD TOKENS
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#d1e7dd' }}>
                        {currentLanguage === 'mr' ? 'कृपया बिल पावती टोकन तपासा' : currentLanguage === 'hi' ? 'कृपया रसीद टोकन जांचें' : 'Please check your receipt token'}
                    </Typography>
                </Box>

                <Grid container>
                    {/* Preparing Section */}
                    <Grid item xs={12} sm={6} sx={{ borderRight: '1px solid #1e293b', p: 4 }}>
                        <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 900, mb: 4, borderBottom: '2px solid #334155', pb: 1, textAlign: 'center' }}>
                            {currentLanguage === 'mr' ? 'ऑर्डर तयार होत आहे' : currentLanguage === 'hi' ? 'तैयार हो रहा है' : 'PREPARING'}
                        </Typography>
                        <Grid container spacing={3} justifyContent="center">
                            {preparingTokens.map((token) => (
                                <Grid item key={token}>
                                    <Box sx={{ 
                                        width: 100, 
                                        height: 100, 
                                        borderRadius: 3, 
                                        bgcolor: '#1e293b', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        border: '2px solid #b45309'
                                    }}>
                                        <Typography variant="h3" sx={{ fontWeight: 900, color: '#f59e0b' }}>
                                            {token}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Ready Section */}
                    <Grid item xs={12} sm={6} sx={{ p: 4 }}>
                        <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 900, mb: 4, borderBottom: '2px solid #334155', pb: 1, textAlign: 'center' }}>
                            {currentLanguage === 'mr' ? 'कृपया घेऊन जा' : currentLanguage === 'hi' ? 'तैयार है / प्राप्त करें' : 'READY / COLLECT'}
                        </Typography>
                        <Grid container spacing={3} justifyContent="center">
                            {readyTokens.map((token) => (
                                <Grid item key={token}>
                                    <Box sx={{ 
                                        width: 120, 
                                        height: 120, 
                                        borderRadius: 3, 
                                        bgcolor: '#064e3b', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        border: '4px solid #10b981',
                                        animation: 'blink 1.2s infinite'
                                    }}>
                                        <Typography variant="h2" sx={{ fontWeight: 900, color: '#10b981' }}>
                                            {token}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Simulation Controller Panel */}
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, boxShadow: 'none' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#008751' }}>
                    ⚙️ {currentLanguage === 'mr' ? 'टोकन चाचणी सिम्युलेटर' : currentLanguage === 'hi' ? 'टोकन परीक्षण सिम्युलेटर' : 'Token Display Simulation Controller'}
                </Typography>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label="New Token ID"
                                size="small"
                                value={newToken}
                                onChange={(e) => setNewToken(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            />
                            <Button variant="contained" onClick={handleAddToken} sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}>
                                Add
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            {currentLanguage === 'mr' ? 'किचन ऑपरेशन्स सिम्युलेट करा:' : currentLanguage === 'hi' ? 'किचन ऑपरेशन्स सिमुलेट करें:' : 'Simulate Kitchen Action (Mark Ready):'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {preparingTokens.map((token) => (
                                <Chip 
                                    key={token} 
                                    label={`Mark ${token} Ready`} 
                                    clickable 
                                    onClick={() => handleMarkReady(token)}
                                    color="warning" 
                                    icon={<PlayArrowIcon />} 
                                    sx={{ fontWeight: 700 }}
                                />
                            ))}
                            {readyTokens.map((token) => (
                                <Chip 
                                    key={token} 
                                    label={`Deliver ${token}`} 
                                    clickable 
                                    onClick={() => handleDeliverToken(token)}
                                    color="success" 
                                    sx={{ fontWeight: 700 }}
                                />
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {/* CSS Animations style definition */}
            <style>{`
                @keyframes blink {
                    0% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.7; transform: scale(1.03); }
                    100% { opacity: 1; transform: scale(1); }
                }
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.01); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </Box>
    );
}
